import {
  AppRootElementBlueprint,
  PageBlueprint,
  SubPageBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  IconBundleBlueprint,
  NavContentBlueprint,
  SignInPageBlueprint,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { Icon } from '@internal/ui';
import { UnifiedThemeProvider } from '@backstage/theme';
import { primaryDarkTheme, primaryLightTheme } from './theme/primaryTheme';
import { PRIMARY_ICONS } from './theme/icons';
import { NavContent } from './components/Root';
import { githubAuthApi } from './apis';
import {
  dateStartedCatalogFilter,
  groupIconEntityCard,
  jobInfoEntityCard,
  joinDateEntityCard,
} from './components/catalog/catalogExtensions';

// AppRoot already renders the OAuth dialog and alert toasts, and plugin-home
// ships the visit listener; SignalsDisplay has no default extension.
const signalsDisplay = AppRootElementBlueprint.make({
  name: 'signals-display',
  params: { element: <SignalsDisplay /> },
});

const navContent = NavContentBlueprint.make({
  params: { component: NavContent },
});

const primaryLight = ThemeBlueprint.make({
  name: 'primary-light',
  params: {
    theme: {
      id: 'primary-light',
      title: 'Primary Light',
      variant: 'light',
      icon: <Icon name="light_mode" size={18} />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={primaryLightTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

const primaryDark = ThemeBlueprint.make({
  name: 'primary-dark',
  params: {
    theme: {
      id: 'primary-dark',
      title: 'Primary Dark',
      variant: 'dark',
      icon: <Icon name="dark_mode" size={18} />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={primaryDarkTheme}>
          {children}
        </UnifiedThemeProvider>
      ),
    },
  },
});

const primaryIcons = IconBundleBlueprint.make({
  name: 'primary-icons',
  params: { icons: PRIMARY_ICONS },
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: () => import('./components/SignInPage').then(m => m.SignInPage),
  },
});

const versionSettingsTab = SubPageBlueprint.make({
  name: 'version',
  attachTo: { id: 'page:user-settings', input: 'pages' },
  params: {
    path: 'version',
    title: 'Version',
    loader: () =>
      import('./components/version/VersionSettingsContent').then(m => (
        <m.VersionSettingsContent />
      )),
  },
});

// Built inline in the ternary so the bundler drops the import() from
// production builds, not just the registration.
const devOnlyExtensions =
  process.env.NODE_ENV === 'production'
    ? []
    : [
        PageBlueprint.make({
          name: 'controls-playground',
          params: {
            path: '/controls',
            noHeader: true,
            loader: () =>
              import('./components/controls/ControlsPlaygroundPage').then(m => (
                <m.ControlsPlaygroundPage />
              )),
          },
        }),
      ];

export const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    navContent,
    signalsDisplay,
    primaryLight,
    primaryDark,
    primaryIcons,
    signInPage,
    githubAuthApi,
    versionSettingsTab,
    groupIconEntityCard,
    jobInfoEntityCard,
    joinDateEntityCard,
    dateStartedCatalogFilter,
    ...devOnlyExtensions,
  ],
});
